// Features: M004-F0807, M004-F0808 (WebUI #270, slice B1: the pure model)
// Tests: T-M004-0466 (model rows), T-M004-0469 (refusal mapping). See calendarUpdateModel.test.js beside this file.
//
// Pure functions only: no React, no HTTP. The proposal shapes are the pinned ProposalRecord, ProposalChange* and
// CorrectionReviewDetail schemas (tests/ledger-account-gateway/pinned/openapi.json, contract 1.29.0, merged Spec !140 @73f28342).
// The WebUI never computes a diff: current and proposed come from the reply.

// RELEASE GATE (M004-F0808, design section 8). The accept and link controls stay hidden while this is false. It is flipped in
// code only after c-core confirms that the approved Core prod artifact carries Core #567, Core #550 and the explicit proposal.* CSLPT rows,
// and never before CALENDAR_UPDATE_VIEW_ENABLED.
// Not an env var and not a build flag. Tests that exercise the accept path force the gate on by their own means.
export const CALENDAR_UPDATE_ACCEPT_ENABLED = false;

// VIEW GATE (M004-F0807, design section 8, owner monitor 2026-10-20). The read-only badge, list, Review update and
// correction-review status expose private booking and customer data, so they stay hidden while this is false. It is
// flipped in code only after c-core confirms that the approved Core prod artifact carries BOTH Core #567 (fail-closed
// relay) and Core #550 (corrected target-org authorization). Same rules as the accept gate: not an env var, not a build flag.
export const CALENDAR_UPDATE_VIEW_ENABLED = false;

export const REASON_MAX_LENGTH = 1000;

// Badge: "Update available (N)" from the reply's pending_count. Null (no badge) when the count is not a usable number or is zero.
export const badgeLabel = (pendingCount) => (
  Number.isInteger(pendingCount) && pendingCount > 0 ? `Update available (${pendingCount})` : null
);

// Groups the listed proposals by customer, for the per-customer view. Customers sort by display name, then id.
// This groups what the list returned; the badge count is pending_count from the reply, never the length of this list.
export const groupProposalsByCustomer = (proposals = []) => {
  const groups = new Map();
  for (const proposal of proposals) {
    const id = proposal.customer_user_id;
    if (!groups.has(id)) groups.set(id, { customer_user_id: id, customer_display_name: proposal.customer_display_name || '', proposals: [] });
    groups.get(id).proposals.push(proposal);
  }
  return [...groups.values()].sort((a, b) => (a.customer_display_name.localeCompare(b.customer_display_name)) || (a.customer_user_id - b.customer_user_id));
};

// Money from minor units with integer arithmetic only (never a float). GBP by default.
const SYMBOL = { GBP: '£', EUR: '€', USD: '$' };
export const formatMinor = (minor, currency = 'GBP') => {
  if (!Number.isInteger(minor)) return '';
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  const pounds = Math.trunc(abs / 100);
  const pence = String(abs % 100).padStart(2, '0');
  return `${sign}${SYMBOL[currency] || `${currency} `}${String(pounds).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${pence}`;
};

// The time as the contract gives it (UTC instant), trimmed to minutes, with its timezone when given. No locale or Date arithmetic.
const clock = (iso) => (typeof iso === 'string' ? `${iso.replace('T', ' ').slice(0, 16)}` : '');
const timeText = (value) => {
  if (!value) return '';
  const zone = value.timezone ? ` ${value.timezone}` : '';
  return `${clock(value.start)} to ${clock(value.end)}${zone}`;
};

export const CHANGE_LABELS = {
  amount: 'Amount',
  time: 'Time',
  description: 'Description',
  bill_to: 'Bill to',
  customer: 'Customer',
  cancellation: 'Cancellation',
  replacement: 'Replacement',
};

// One row per change: the label, the current Ledger value beside the proposed Core value, and the arrow text.
// Unknown kinds show their label as "Other" and no values (the union is closed, so this is defensive only).
const valueText = (kind, value) => {
  if (!value || typeof value !== 'object') return '';
  switch (kind) {
    case 'amount': return formatMinor(value.amount_minor, value.currency);
    case 'time': return timeText(value);
    case 'description': return value.description || '';
    case 'bill_to': return value.display_name || '';
    case 'customer': return value.display_name || (Number.isInteger(value.user_id) ? `Customer ${value.user_id}` : '');
    case 'cancellation': return value.cancelled ? `Cancelled${value.cancelled_on ? ` on ${value.cancelled_on}` : ''}` : 'Not cancelled';
    case 'replacement': return value.series_id ? `Series ${value.series_id}${value.occurrence_date ? ` on ${value.occurrence_date}` : ''}` : '';
    default: return '';
  }
};

export const formatChange = (change) => {
  const label = CHANGE_LABELS[change?.kind] || 'Other';
  const current = valueText(change?.kind, change?.current);
  const proposed = valueText(change?.kind, change?.proposed);
  return { kind: change?.kind, label, current, proposed, text: `${current} → ${proposed}` };
};

export const formatChanges = (proposal) => (proposal?.changes || []).map(formatChange);

// item_state: unwritten = accept applies the change; written = accept opens a Ledger correction review instead.
export const ITEM_STATE_TEXT = {
  unwritten: 'Not yet on an invoice; accepting applies this change.',
  written: 'Already on an invoice; accepting opens a correction review.',
};
export const itemStateText = (itemState) => ITEM_STATE_TEXT[itemState] || '';
export const acceptLeadsToCorrectionReview = (proposal) => proposal?.item_state === 'written';

// replacement_link: required needs a decision before accept; not_applicable has no link control at all.
export const needsReplacementLink = (proposal) => proposal?.replacement_link === 'required';
export const replacementLinkText = (replacementLink) => ({
  required: 'A replacement series needs a decision before you can accept.',
  linked: 'Linked to the earlier series.',
  separate: 'Kept separate from the earlier series.',
}[replacementLink] || null);
export const showsLinkControl = (proposal) => proposal?.replacement_link !== 'not_applicable' && proposal?.replacement_link !== undefined;

// Reason: required, trimmed, 1..1000 characters, with at least one non-space character (the contract's bounds).
export const REASON_REQUIRED = 'Enter a reason before you accept this update.';
export const validateReason = (text) => {
  const reason = typeof text === 'string' ? text.trim() : '';
  if (!reason) return { ok: false, message: REASON_REQUIRED };
  if (reason.length > REASON_MAX_LENGTH) return { ok: false, message: `The reason must be ${REASON_MAX_LENGTH} characters or fewer.` };
  return { ok: true, reason };
};

const GENERIC = 'This could not be done. Check the page and try again.';
const PERMISSION = 'You do not have permission to review calendar updates.';
const SAL = 'This needs a stronger sign-in. Confirm it, then try again.';

// Refusal code -> sentence. The server's own message is never read or shown.
const SENTENCES = {
  PROPOSAL_REVISION_STALE: 'This update has changed since you opened it. The latest version is shown below; check it, then accept again.',
  PROPOSAL_REPLACEMENT_LINK_REQUIRED: 'Choose whether this replacement is linked or separate before accepting.',
  PROPOSAL_LINK_NOT_APPLICABLE: 'There is no replacement series to link for this update.',
  PROPOSAL_NOT_FOUND: 'That calendar update is no longer available. The list has been refreshed.',
  LEDGER_HA_IDEMPOTENCY_CONFLICT: 'This change was already sent with different details. Check the page and try again.',
  CORRECTION_REVIEW_NOT_FOUND: 'That correction review is no longer available.',
  LEDGER_REASON_REQUIRED: 'Enter a reason before you accept this update.',
  LEDGER_OWN_ACCOUNT_REFUSED: 'You cannot accept a change to your own account. Ask another manager.',
  LEDGER_ACCOUNT_PERMISSION_DENIED: PERMISSION,
  LEDGER_ACCOUNT_AUTHORITY_REQUIRED: PERMISSION,
  LEDGER_ACCOUNT_READONLY: PERMISSION,
  LEDGER_ACCOUNT_SAL_REQUIRED: SAL,
};

// The code the server sent, read from the error body (the closed refusal envelope uses `code`; the relay uses `error`).
export const refusalCode = (error) => {
  const data = error?.response?.data || {};
  if (typeof data.error === 'string') return data.error;
  return data.error?.code || data.code || '';
};

export const refusalSentence = (error) => {
  const code = refusalCode(error);
  const details = error?.response?.data?.details || {};
  if (code === 'PROPOSAL_ALREADY_DECIDED' && details.state && details.decided_at) {
    return `This update was already decided on ${details.decided_at} (state: ${details.state}).`;
  }
  return SENTENCES[code] || GENERIC;
};

// Correction review status from the correction_review.get detail: open, or resolved as approved | dismissed.
export const correctionReviewStatus = (review) => {
  if (review?.state === 'open') return 'open';
  if (review?.state === 'resolved' && review?.resolution === 'approved') return 'approved';
  if (review?.state === 'resolved' && review?.resolution === 'dismissed') return 'dismissed';
  return null;
};

const REVIEW_TEXT = {
  open: 'Correction review is open. Nothing has changed on the invoice yet.',
  approved: 'Correction review approved.',
  dismissed: 'Correction review dismissed. The invoice was not changed.',
};

// Documents are named by number and shown only when approved (the contract carries none before approval).
export const correctionReviewText = (status, documents = []) => {
  const text = REVIEW_TEXT[status];
  if (!text) return null;
  if (status !== 'approved') return text;
  const numbers = (documents || []).map((document) => document.number).filter(Boolean);
  return numbers.length ? `${text} Documents: ${numbers.join(', ')}.` : text;
};
